/**
 * `router` — the routes of `spec.md` §13.10, plus the two screens added after it.
 *
 * No router library. Twelve routes, three of which take one numeric id, in an app that is
 * a single origin with an SPA fallback on the server (`spec.md` §7) and a `startapp`
 * deep link in front of it — a dependency here would be more surface than the feature.
 *
 * | route | screen | primary API |
 * |---|---|---|
 * | `/` | Crate | `GET /api/catalogue` |
 * | `/now` | Now Playing | `GET /api/tracks/:id`, `/peaks` |
 * | `/search` | Search + drawer index | `GET /api/search` |
 * | `/queue` | SET LIST sheet | `GET/PUT/POST /api/queue` |
 * | `/crate` | MY CRATE | `GET /api/favorites` |
 * | `/crate/playlists/:id` | Playlist | `GET /api/playlists/:id` |
 * | `/artist/:id` | Artist | `GET /api/artists/:id` |
 * | `/tag/:id` | Tag | `GET /api/tags/:id` |
 * | `/history` | Recently played | `GET /api/history` |
 * | `/pads` | PADS — the sample bank and the siren | `GET /api/pads/sounds`, `/kit` |
 * | `/skins` | ОФОРМЛЕНИЕ — the skin picker | none; the choice is client-side |
 * | `/admin` | Admin | `/api/admin/*` |
 *
 * ## Path, hash, or both
 *
 * The server serves `index.html` for every non-`/api` path, so `/artist/12` is a real
 * URL and `history.pushState` is the primary mode. A `#/…` fragment is still honoured
 * on read and is the fallback when `pushState` is unavailable, because a Mini App can
 * be opened through a link whose path is fixed by BotFather and whose only writable
 * part is the fragment. {@link readPath} treats the two identically, so nothing
 * downstream has to know which one is in play.
 *
 * ## How the screens are resolved, and why it is a glob
 *
 * This module is written **barrel-first**: it names all ten screens before any of them
 * exists. A static `import { Crate } from '../screens/Crate'` would make that
 * literally unloadable — Vite resolves every static *and* dynamic specifier at
 * transform time and fails the module — so no test, including this router's own, could
 * run until the last screen landed.
 *
 * `import.meta.glob` is the one form Vite resolves against the filesystem rather than
 * against a specifier: it yields `{}` when nothing matches, picks the screens up the
 * moment they are created with no edit here, and still statically analyses in the
 * production build. Each route names its module path and its exported symbol, so the
 * contract with T40–T46 is spelled out in the table above rather than implied by an
 * import list — and {@link Router} takes a `screens` override, which is how a test
 * drives all ten routes without any of them on disk.
 *
 * **The contract for a screen module**: export a component named exactly as the table
 * says, taking {@link ScreenProps}. Route params arrive as strings, because a URL has
 * no numbers in it.
 */

import { createElement, useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { ComponentType, JSX } from 'react';

import type { ApiFailure } from '../lib/api';
import { USER_MESSAGE_RU, displayCode } from '../lib/api';
import { telegram } from '../lib/telegram';

import { ErrorState } from './states/ErrorState';

/* ══ the table ═════════════════════════════════════════════════════════════════════ */

/** Route params, always strings: a URL has no numbers in it. */
export type RouteParams = Readonly<Record<string, string>>;

/** The props every screen module's component is handed. */
export interface ScreenProps {
  readonly route: RouteName;
  readonly params: RouteParams;
}

export type ScreenComponent = ComponentType<ScreenProps>;

export interface RouteDefinition {
  /** Stable identity, used by the nav strip and by `persist`'s `tab` preference. */
  readonly name: RouteName;
  /** The path pattern. `:name` is one segment of parameter. */
  readonly path: string;
  /** The module the screen lives in, as `import.meta.glob` keys it. */
  readonly module: string;
  /** The exported symbol inside that module. */
  readonly screen: string;
  /**
   * `true` for the three nav-strip roots — `ЯЩИК` · `ПОИСК` · `МОЁ` (§9.2). Telegram's
   * BackButton is shown on every other route, because §10.1 puts it on "any screen
   * deeper than a tab root".
   */
  readonly tabRoot: boolean;
}

export type RouteName =
  | 'crate'
  | 'now'
  | 'search'
  | 'queue'
  | 'favorites'
  | 'playlist'
  | 'artist'
  | 'tag'
  | 'history'
  | 'pads'
  | 'skins'
  | 'admin';

export const ROUTES: readonly RouteDefinition[] = Object.freeze([
  {
    name: 'crate',
    path: '/',
    module: '../screens/Crate.tsx',
    screen: 'Crate',
    tabRoot: true,
  },
  {
    name: 'now',
    path: '/now',
    module: '../screens/NowPlaying.tsx',
    screen: 'NowPlaying',
    tabRoot: false,
  },
  {
    name: 'search',
    path: '/search',
    module: '../screens/Search.tsx',
    screen: 'Search',
    tabRoot: true,
  },
  {
    name: 'queue',
    path: '/queue',
    module: '../screens/SetList.tsx',
    screen: 'SetList',
    tabRoot: false,
  },
  {
    name: 'favorites',
    path: '/crate',
    module: '../screens/MyCrate.tsx',
    screen: 'MyCrate',
    tabRoot: true,
  },
  {
    name: 'playlist',
    path: '/crate/playlists/:id',
    module: '../screens/Playlist.tsx',
    screen: 'Playlist',
    tabRoot: false,
  },
  {
    name: 'artist',
    path: '/artist/:id',
    module: '../screens/Artist.tsx',
    screen: 'Artist',
    tabRoot: false,
  },
  {
    name: 'tag',
    path: '/tag/:id',
    module: '../screens/Tag.tsx',
    screen: 'Tag',
    tabRoot: false,
  },
  {
    name: 'history',
    path: '/history',
    module: '../screens/History.tsx',
    screen: 'History',
    tabRoot: false,
  },
  {
    /*
     * The fourth nav root. `tabRoot: true` keeps Telegram's BackButton hidden (§10.1),
     * which is what makes it read as a place rather than a sub-page of the crate.
     * `design-direction.md` §9.2 used to say the strip had three items and would never
     * have a fourth; the operator asked for this screen and chose to make it a
     * destination, and that paragraph now says four.
     */
    name: 'pads',
    path: '/pads',
    module: '../screens/Pads.tsx',
    screen: 'Pads',
    tabRoot: true,
  },
  {
    /*
     * ОФОРМЛЕНИЕ — the skin picker. `tabRoot: false`, so Telegram's BackButton is shown
     * (§10.1): it is a place you go from МОЁ and come back from, not a fifth root. The
     * nav strip stays at four items.
     */
    name: 'skins',
    path: '/skins',
    module: '../screens/Skins.tsx',
    screen: 'Skins',
    tabRoot: false,
  },
  {
    name: 'admin',
    path: '/admin',
    module: '../screens/Admin.tsx',
    screen: 'Admin',
    tabRoot: false,
  },
]);

/** Where an unknown path lands: the crate, which is also `/`. */
export const FALLBACK_ROUTE: RouteName = 'crate';

const EMPTY_PARAMS: RouteParams = Object.freeze({});

/* ══ matching ══════════════════════════════════════════════════════════════════════ */

export interface RouteMatch {
  readonly route: RouteName;
  readonly definition: RouteDefinition;
  readonly params: RouteParams;
  /** The normalised path this match came from. */
  readonly path: string;
  /** `false` when the path matched nothing and the crate was substituted. */
  readonly matched: boolean;
}

/** `/a/b/` and `/a/b?x=1#y` both normalise to `/a/b`; anything empty becomes `/`. */
export function normalisePath(input: string): string {
  const withoutHash = input.split('#')[0] ?? '';
  const withoutQuery = withoutHash.split('?')[0] ?? '';
  const trimmed = withoutQuery.trim();
  if (trimmed === '' || trimmed === '/') return '/';
  const prefixed = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return prefixed.length > 1 && prefixed.endsWith('/') ? prefixed.slice(0, -1) : prefixed;
}

function segmentsOf(path: string): string[] {
  return path === '/' ? [] : path.slice(1).split('/');
}

/**
 * The route for a path, falling back to the crate.
 *
 * Segment count is compared first, so `/crate` and `/crate/playlists/7` can never be
 * confused, and literal segments must match exactly — a parameter never swallows a
 * literal. An unknown path is not an error state: it is the crate, because a Mini App
 * opened from a stale deep link should land somewhere useful rather than on a plate
 * that says the link was wrong.
 */
export function matchRoute(input: string): RouteMatch {
  const path = normalisePath(input);
  const parts = segmentsOf(path);

  for (const definition of ROUTES) {
    const pattern = segmentsOf(definition.path);
    if (pattern.length !== parts.length) continue;

    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < pattern.length; i += 1) {
      const expected = pattern[i] ?? '';
      const actual = parts[i] ?? '';
      if (expected.startsWith(':')) {
        if (actual === '') {
          ok = false;
          break;
        }
        params[expected.slice(1)] = decodeURIComponent(actual);
        continue;
      }
      if (expected !== actual) {
        ok = false;
        break;
      }
    }
    if (!ok) continue;

    return {
      route: definition.name,
      definition,
      params: Object.keys(params).length === 0 ? EMPTY_PARAMS : Object.freeze(params),
      path,
      matched: true,
    };
  }

  const fallback = ROUTES.find((route) => route.name === FALLBACK_ROUTE) ?? ROUTES[0];
  /* ROUTES is a non-empty frozen literal; this branch exists for the type only. */
  if (fallback === undefined) throw new Error('router: ROUTES is empty');
  return {
    route: fallback.name,
    definition: fallback,
    params: EMPTY_PARAMS,
    path,
    matched: false,
  };
}

/** The path a route name (plus params) points at: `buildPath('artist', {id:'12'})`. */
export function buildPath(name: RouteName, params: RouteParams = EMPTY_PARAMS): string {
  const definition = ROUTES.find((route) => route.name === name);
  if (definition === undefined) return '/';
  const filled = segmentsOf(definition.path).map((segment) =>
    segment.startsWith(':') ? encodeURIComponent(params[segment.slice(1)] ?? '') : segment,
  );
  return filled.length === 0 ? '/' : `/${filled.join('/')}`;
}

/* ══ location ══════════════════════════════════════════════════════════════════════ */

/** The two fields of `window.location` this module reads. */
export interface LocationLike {
  readonly pathname: string;
  readonly hash: string;
}

/**
 * The current path, from the fragment when there is one and from the pathname
 * otherwise.
 *
 * A `#/…` fragment wins because it can only have been written deliberately — by us,
 * or by a link whose path part is fixed by BotFather.
 */
export function readPath(location?: LocationLike): string {
  const source: LocationLike | undefined =
    location ?? (typeof window === 'undefined' ? undefined : window.location);
  if (source === undefined) return '/';
  const hash = source.hash;
  if (hash.startsWith('#/')) return normalisePath(hash.slice(1));
  return normalisePath(source.pathname);
}

/**
 * A `pushState` does not fire `popstate`, so navigation inside the app is announced
 * on this event instead. It is dispatched on `window`, which is also where `popstate`
 * and `hashchange` arrive, so one subscription covers all three.
 */
export const NAVIGATE_EVENT = 'bl:navigate';

export interface NavigateOptions {
  /** Replace the current entry instead of pushing a new one. */
  readonly replace?: boolean;
}

/**
 * Go to `to` — a path, or a route name with params via {@link buildPath}.
 *
 * Falls back to writing the fragment when `pushState` is unavailable or refuses (an
 * exotic webview, a sandboxed frame), which keeps navigation working rather than
 * throwing inside a click handler.
 */
export function navigate(to: string, options: NavigateOptions = {}): void {
  if (typeof window === 'undefined') return;
  const path = normalisePath(to);
  if (readPath() === path) return;

  const history = window.history;
  const canPush = typeof history?.pushState === 'function';
  if (canPush) {
    try {
      if (options.replace === true) history.replaceState(null, '', path);
      else history.pushState(null, '', path);
      window.dispatchEvent(new Event(NAVIGATE_EVENT));
      return;
    } catch {
      /* Fall through to the fragment. */
    }
  }
  window.location.hash = `#${path}`;
}

function subscribeToLocation(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('popstate', onChange);
  window.addEventListener('hashchange', onChange);
  window.addEventListener(NAVIGATE_EVENT, onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener('hashchange', onChange);
    window.removeEventListener(NAVIGATE_EVENT, onChange);
  };
}

/** The live path. A string snapshot, so `useSyncExternalStore` never loops. */
export function usePath(): string {
  return useSyncExternalStore(
    subscribeToLocation,
    () => readPath(),
    () => '/',
  );
}

/** The live route match. */
export function useRoute(): RouteMatch {
  const path = usePath();
  return useMemo(() => matchRoute(path), [path]);
}

/* ══ screen resolution ═════════════════════════════════════════════════════════════ */

/** A screen module: whatever it exports, read by name. */
type ScreenModule = Readonly<Record<string, unknown>>;

/**
 * Every screen module on disk, keyed by the path in {@link ROUTES}.
 *
 * Empty until T40–T46 land, which is exactly the point: the router loads, and its
 * tests run, against the `screens` override.
 */
const SCREEN_MODULES: Readonly<Record<string, ScreenModule>> = import.meta.glob<ScreenModule>(
  '../screens/*.tsx',
  { eager: true },
);

export type ScreenTable = Partial<Readonly<Record<RouteName, ScreenComponent>>>;

/** The screen component for a route, or `null` when its module is not on disk yet. */
export function resolveScreen(
  definition: RouteDefinition,
  overrides?: ScreenTable,
): ScreenComponent | null {
  const override = overrides?.[definition.name];
  if (override !== undefined) return override;

  const found = SCREEN_MODULES[definition.module]?.[definition.screen];
  return typeof found === 'function' ? (found as ScreenComponent) : null;
}

/**
 * The plate shown when a screen module is missing.
 *
 * That is a build fault rather than a user fault, so it prints the same catalogue code
 * and the same plain sentence as any other internal failure instead of inventing a
 * developer-facing one — and it is deliberately not retryable, because a retry cannot
 * bring a missing module back.
 */
const MISSING_SCREEN: ApiFailure = Object.freeze({
  code: 'INTERNAL',
  message: USER_MESSAGE_RU.INTERNAL,
  displayCode: displayCode('INTERNAL'),
  status: 0,
  retryable: false,
});

/* ══ Router ════════════════════════════════════════════════════════════════════════ */

export interface RouterProps {
  /**
   * Screens to use instead of the ones on disk. A test passes all ten; the app passes
   * none.
   */
  readonly screens?: ScreenTable;
  /**
   * Called after every route change with the new match — the hook `main.tsx` uses to
   * persist the last tab and to re-assert Telegram chrome.
   */
  readonly onRouteChange?: ((match: RouteMatch) => void) | undefined;
}

/**
 * Renders the screen for the current route, and drives Telegram's BackButton.
 *
 * The back button is shown on every route that is not one of the three nav-strip
 * roots (§9.2, §10.1) and goes back through history — falling back to the crate when
 * there is nothing to go back to, which is what happens when the Mini App was opened
 * straight onto a deep link.
 */
export function Router({ screens, onRouteChange }: RouterProps): JSX.Element {
  const match = useRoute();
  const { definition } = match;

  useEffect(() => {
    onRouteChange?.(match);
  }, [match, onRouteChange]);

  const goBack = useCallback((): void => {
    if (typeof window === 'undefined') return;
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    navigate(buildPath(FALLBACK_ROUTE));
  }, []);

  useEffect(() => {
    if (definition.tabRoot) {
      telegram.backButton.hide();
      return;
    }
    const off = telegram.backButton.onClick(goBack);
    telegram.backButton.show();
    return () => {
      off();
      telegram.backButton.hide();
    };
  }, [definition, goBack]);

  /*
   * Lower-case, and rendered through `createElement`: the component is *looked up*,
   * not created, but a capitalised local assigned from a call during render is exactly
   * the shape `react-hooks/static-components` (rightly) reports, and the lint cannot
   * tell a lookup from a factory.
   */
  const component = resolveScreen(definition, screens);

  if (component === null) {
    if (import.meta.env.DEV) {
      console.error(
        `router: no screen for ${definition.name} — expected ${definition.screen} ` +
          `exported from ${definition.module}.`,
      );
    }
    return <ErrorState failure={MISSING_SCREEN} grid="plain" />;
  }

  return createElement(component, { route: match.route, params: match.params });
}
